import * as ss from 'superstruct';
import { AdSources } from './paid-source.resource';

export interface AdAccountIdRule {
  /** What the ad platform calls the id, for user-facing messages. */
  label: string;
  /** Canonical form of a trimmed, non-blank id. */
  normalize: (trimmed: string) => string;
  /** User-facing error if a normalized id isn't in the platform's format. */
  validate: (normalized: string) => string | undefined;
}

const rule = ({
  label,
  normalize = (id) => id,
  format,
}: {
  label: string;
  normalize?: (trimmed: string) => string;
  format?: { pattern: RegExp; requirement: string };
}): AdAccountIdRule => ({
  label,
  normalize,
  validate: (id) =>
    !format || format.pattern.test(id)
      ? undefined
      : `${label} ${format.requirement}`,
});

const digitsOnly = {
  pattern: /^\d+$/,
  requirement: 'must contain only digits',
};

/**
 * Per ad source, how to reduce an account id to the form the ad platform's API
 * and insights data use, and what that form looks like.
 *
 * Keep identical to au_dbt's `macros/configs/normalize_account_ids.sql`.
 */
export const AdAccountIdRules: Record<AdSources, AdAccountIdRule> = {
  google: rule({
    label: 'Google Ads customer ID',
    normalize: (id) => id.replace(/-/g, ''),
    format: { pattern: /^\d{10}$/, requirement: 'must be 10 digits' },
  }),
  meta: rule({
    label: 'Meta ad account ID',
    normalize: (id) => id.replace(/^act_/, ''),
    format: digitsOnly,
  }),
  // The Microsoft destination field is named customerId, but it holds the
  // AccountId: it's sent as the CustomerAccountId header.
  bing: rule({ label: 'Microsoft Ads account ID', format: digitsOnly }),
  linkedin: rule({ label: 'LinkedIn account ID' }),
  reddit: rule({ label: 'Reddit account ID' }),
  openai: rule({ label: 'OpenAI ads account ID' }),
  email: rule({ label: 'Account ID' }),
};

/**
 * Canonical form of `raw` for `adSource`. Blank means no account id, so
 * returns `undefined`.
 */
export const normalizeAdAccountId = (
  adSource: AdSources,
  raw: string | null | undefined,
): string | undefined => {
  const trimmed = raw?.trim();
  return trimmed ? AdAccountIdRules[adSource].normalize(trimmed) : undefined;
};

/**
 * User-facing error if `raw`, once normalized, isn't a valid account id for
 * `adSource`. A blank id is valid unless `required`.
 */
export const validateAdAccountId = (
  adSource: AdSources,
  raw: string | null | undefined,
  { required = false }: { required?: boolean } = {},
): string | undefined => {
  const { label, validate } = AdAccountIdRules[adSource];
  const id = normalizeAdAccountId(adSource, raw);
  if (id === undefined) {
    return required ? `${label} is required` : undefined;
  }
  return validate(id);
};

/**
 * A required account id for `adSource`. `ss.create` and `ss.mask` normalize
 * it. Every check rejects a blank id or one that is invalid once normalized,
 * so un-normalized but valid ids (e.g. `123-456-7890`) still pass `ss.is`.
 */
export const AdAccountId = (adSource: AdSources) =>
  ss.coerce(
    ss.refine(
      ss.string(),
      'AdAccountId',
      (value) =>
        validateAdAccountId(adSource, value, { required: true }) ?? true,
    ),
    ss.string(),
    (value) => normalizeAdAccountId(adSource, value) ?? value,
  );

export const GoogleAdsCustomerId = AdAccountId('google');
export const MetaAdAccountId = AdAccountId('meta');
export const MicrosoftAdsCustomerId = AdAccountId('bing');
export const OpenAiAdsAccountId = AdAccountId('openai');
